import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/lib/auth";
import { apiClient, readError } from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, Send, Copy, Check, Trash2, Hammer, User as UserIcon, Clock, Mail } from "lucide-react";
import { CATEGORIES } from "@/lib/categories";

const SPECIALTIES = CATEGORIES.filter((c) => c !== "Autre");

export default function Invitations() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);
  const [form, setForm] = useState({
    role: user?.role === "demandeur" ? "artisan" : "demandeur",
    name: "",
    email: "",
    phone: "",
    company: "",
    city: "",
    message: "",
  });
  const [specs, setSpecs] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await apiClient.get("/invitations/me");
      setList(data.invitations || []);
    } catch (e) {
      toast.error(readError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, specialties: form.role === "artisan" ? specs : [] };
      await apiClient.post("/invitations", payload);
      toast.success("Invitation créée");
      setForm({ ...form, name: "", email: "", phone: "", company: "", city: "", message: "" });
      setSpecs([]);
      load();
    } catch (e) {
      toast.error(readError(e));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id) => {
    if (!window.confirm("Annuler cette invitation ?")) return;
    try {
      await apiClient.delete(`/invitations/${id}`);
      toast.success("Invitation annulée");
      load();
    } catch (e) {
      toast.error(readError(e));
    }
  };

  const copyLink = async (inv) => {
    const url = `${window.location.origin}/invitation/${inv.token}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(inv.id);
      toast.success("Lien copié !");
      setTimeout(() => setCopiedId(null), 1800);
    } catch {
      toast.error("Impossible de copier — sélectionnez manuellement");
    }
  };

  const toggleSpec = (s) =>
    setSpecs((p) => (p.includes(s) ? p.filter((x) => x !== s) : [...p, s]));

  return (
    <div className="min-h-screen bg-[#F7F7F9]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00]">// Invitations</div>
        <h1 className="display text-5xl mt-2">Inviter une partie</h1>
        <p className="text-zinc-700 mt-3 max-w-3xl">
          Pré-renseignez les informations de votre {user?.role === "demandeur" ? "artisan" : "demandeur"} et
          partagez-lui le lien généré (SMS, WhatsApp, en main propre…). Il définit son mot de passe et
          rejoint Batisur en un clic.
        </p>

        <div className="mt-10 grid lg:grid-cols-12 gap-6">
          {/* FORM */}
          <form onSubmit={submit} className="lg:col-span-7 border-2 border-[#0A0A0B] bg-white p-7" data-testid="invitation-form">
            <div className="flex items-center gap-2 mono text-[11px] uppercase tracking-widest text-zinc-500">
              <Send className="w-3.5 h-3.5 text-[#FF4D00]" /> // Nouvelle invitation
            </div>

            <div className="grid grid-cols-2 gap-3 mt-4">
              <button
                type="button"
                onClick={() => setForm({ ...form, role: "demandeur" })}
                className={`border-2 p-4 text-left ${form.role === "demandeur" ? "bg-[#FF4D00] text-white border-[#FF4D00]" : "bg-white text-[#0A0A0B] border-[#0A0A0B]"}`}
                data-testid="inv-role-demandeur"
              >
                <UserIcon className="w-5 h-5" />
                <div className="display text-lg mt-2">Demandeur</div>
                <div className="text-xs mt-0.5 opacity-80">Particulier souhaitant des travaux</div>
              </button>
              <button
                type="button"
                onClick={() => setForm({ ...form, role: "artisan" })}
                className={`border-2 p-4 text-left ${form.role === "artisan" ? "bg-[#FF4D00] text-white border-[#FF4D00]" : "bg-white text-[#0A0A0B] border-[#0A0A0B]"}`}
                data-testid="inv-role-artisan"
              >
                <Hammer className="w-5 h-5" />
                <div className="display text-lg mt-2">Artisan</div>
                <div className="text-xs mt-0.5 opacity-80">Professionnel du bâtiment</div>
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3 mt-4">
              <Inp label="Nom complet" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testid="inv-name" />
              <Inp label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testid="inv-email" />
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <Inp label="Téléphone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="inv-phone" />
              <Inp label="Ville" value={form.city} onChange={(v) => setForm({ ...form, city: v })} testid="inv-city" />
            </div>
            {form.role === "artisan" && (
              <>
                <div className="mt-3">
                  <Inp label="Société" value={form.company} onChange={(v) => setForm({ ...form, company: v })} testid="inv-company" />
                </div>
                <div className="mt-3">
                  <label className="mono text-[11px] uppercase tracking-widest text-zinc-600">
                    Spécialités ({specs.length})
                  </label>
                  <div className="flex flex-wrap gap-1.5 mt-2 max-h-32 overflow-auto scrollbar-thin border-2 border-zinc-200 p-2">
                    {SPECIALTIES.slice(0, 18).map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => toggleSpec(s)}
                        className={`px-2 py-0.5 text-[11px] border-2 mono uppercase tracking-wider ${specs.includes(s) ? "bg-[#0A0A0B] text-white border-[#0A0A0B]" : "bg-white text-[#0A0A0B] border-zinc-300 hover:border-[#0A0A0B]"}`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
            <div className="mt-3">
              <label className="mono text-[11px] uppercase tracking-widest text-zinc-600">Message personnalisé (optionnel)</label>
              <textarea
                rows="3"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder={`Quelques mots pour ${form.role === "artisan" ? "l'artisan" : "le demandeur"}…`}
                className="w-full mt-2 border-2 border-[#0A0A0B] px-4 py-3 bg-white"
                data-testid="inv-message"
              />
            </div>
            <button type="submit" disabled={busy} className="btn-batisur w-full mt-5 flex items-center justify-center gap-2" data-testid="inv-submit">
              <Send className="w-4 h-4" /> {busy ? "Création…" : "Générer l'invitation"}
            </button>
          </form>

          {/* LIST */}
          <aside className="lg:col-span-5 space-y-4">
            <div className="border-2 border-[#0A0A0B] bg-white p-5">
              <div className="display text-2xl flex items-center gap-2">
                <Mail className="w-5 h-5 text-[#FF4D00]" /> Mes invitations
              </div>
              <div className="mono text-[11px] uppercase tracking-widest text-zinc-500 mt-1">
                {list.length} au total · valide 30 jours
              </div>
            </div>

            {loading && <div className="mono text-xs text-zinc-500">Chargement…</div>}
            {!loading && list.length === 0 && (
              <div className="border-2 border-dashed border-zinc-300 p-8 text-center">
                <UserPlus className="w-8 h-8 mx-auto text-[#FF4D00]" />
                <div className="mono text-[11px] uppercase tracking-widest text-zinc-500 mt-2">// Aucune invitation</div>
                <div className="text-sm text-zinc-600 mt-1">Créez votre première à gauche</div>
              </div>
            )}

            {list.map((inv) => (
              <div key={inv.id} className="border-2 border-[#0A0A0B] bg-white p-4" data-testid={`my-inv-${inv.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="display text-lg">{inv.name}</div>
                    <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
                      {inv.role} · {inv.email}
                    </div>
                  </div>
                  <StatusPill status={inv.status} />
                </div>

                {inv.status === "pending" && (
                  <div className="mt-3 grid grid-cols-3 gap-0 border-2 border-[#0A0A0B] text-xs">
                    <div className="p-2 border-r-2 border-[#0A0A0B]">
                      <div className="mono text-[9px] uppercase tracking-widest text-zinc-500">Code</div>
                      <div className="mono font-bold text-sm">{inv.short_code}</div>
                    </div>
                    <div className="p-2 col-span-2">
                      <div className="mono text-[9px] uppercase tracking-widest text-zinc-500">Expire</div>
                      <div className="text-xs mt-0.5">{new Date(inv.expires_at).toLocaleDateString("fr-FR")}</div>
                    </div>
                  </div>
                )}

                <div className="mt-3 flex flex-wrap gap-2">
                  {inv.status === "pending" && (
                    <button
                      onClick={() => copyLink(inv)}
                      className="border-2 border-[#0A0A0B] px-3 py-1.5 mono text-[10px] uppercase tracking-widest hover:bg-[#0A0A0B] hover:text-white flex items-center gap-1"
                      data-testid={`copy-link-${inv.id}`}
                    >
                      {copiedId === inv.id ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                      {copiedId === inv.id ? "Copié" : "Copier le lien"}
                    </button>
                  )}
                  {inv.status === "pending" && (
                    <button
                      onClick={() => revoke(inv.id)}
                      className="border-2 border-red-300 text-red-700 px-3 py-1.5 mono text-[10px] uppercase tracking-widest hover:bg-red-600 hover:text-white hover:border-red-600 flex items-center gap-1"
                      data-testid={`revoke-${inv.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Annuler
                    </button>
                  )}
                </div>
              </div>
            ))}
          </aside>
        </div>
      </div>
    </div>
  );
}

function Inp({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="mono text-[11px] uppercase tracking-widest text-zinc-600">{label}</label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full mt-1.5 border-2 border-[#0A0A0B] px-3 py-2 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#FF4D00]"
        data-testid={testid}
      />
    </div>
  );
}

function StatusPill({ status }) {
  const styles = {
    pending: "bg-amber-50 text-amber-700 border-amber-300",
    claimed: "bg-emerald-50 text-emerald-700 border-emerald-300",
    expired: "bg-zinc-100 text-zinc-600 border-zinc-300",
  };
  const labels = { pending: "En attente", claimed: "Activée", expired: "Expirée" };
  return (
    <span className={`badge-pill ${styles[status] || styles.expired}`}>
      <Clock className="w-3 h-3" /> {labels[status] || status}
    </span>
  );
}
